/**
 * openHelpAgent — open (or focus) the Sam Help Agent popup window.
 *
 * Context tracking uses the server (sam-dbam) as the communication channel:
 *   - Each app POSTs its context to /dbam/v1/help/context on tab focus
 *   - The popup polls GET /dbam/v1/help/context/{userId} every 3 seconds
 *   - No cross-origin browser tricks needed
 */

let _helpPopup = null;
export const HELP_APP_RELEASE = 'help-20260807.1';

function getHelpAppUrl() {
    const isLocal = window.location.hostname === 'localhost'
        || window.location.hostname === '127.0.0.1';
    return isLocal
        ? 'http://localhost:3004'
        : 'https://help.samsurveyor.com';
}

function getDbamUrl() {
    const isLocal = window.location.hostname === 'localhost'
        || window.location.hostname === '127.0.0.1';
    return isLocal
        ? 'http://localhost:8080'
        : 'https://api.samsurveyor.com';
}

/**
 * assistantsAllowed asks sam-dbam whether the assistants may run for this
 * account.
 *
 * The assistants spend LLM tokens and tokens cost money, so a free account that
 * has used up its surveys does not get to keep spending them. sam-dbam answers
 * false in exactly that one case.
 *
 * A failed check returns true. Refusing help because a status call did not
 * answer would punish the wrong person, and the real spending limits still hold
 * inside the assistant itself.
 *
 * Returns { enabled, message }.
 */
export async function assistantsAllowed({ orgId } = {}) {
    if (!orgId) return { enabled: true, message: '' };
    try {
        const resp = await fetch(`${getDbamUrl()}/dbam/v1/limits/assistants`, {
            headers: { 'X-Org-ID': orgId },
        });
        if (!resp.ok) return { enabled: true, message: '' };
        const payload = await resp.json();
        const data = payload?.data ?? payload;
        return { enabled: data?.enabled !== false, message: data?.message || '' };
    } catch (_) {
        return { enabled: true, message: '' };
    }
}

/**
 * Open (or focus) the help agent popup and set the context server-side.
 *
 * When orgId is given the plan is checked first. A used-up free account does not
 * get the assistant, and onRefused is called with the sentence to show. The
 * popup is never opened in that case, so no tokens are spent.
 *
 * The help-agent service itself is unchanged and still answers a direct call.
 * This closes the door the console opens, not the service behind it.
 */
export async function openHelpAgent({ context, userId, userName, orgId, onRefused } = {}) {
    if (orgId) {
        const { enabled, message } = await assistantsAllowed({ orgId });
        if (!enabled) {
            onRefused?.(message || 'The assistants are not available on your current plan.');
            return;
        }
    }
    const helpUrl = getHelpAppUrl();

    // Open or reuse the popup
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (userName) params.set('userName', userName);
    if (context) params.set('context', context);
    params.set('helpBuild', HELP_APP_RELEASE);
    _helpPopup = window.open(
        `${helpUrl}/?${params.toString()}`,
        'sam-help-agent',
        'width=520,height=760,resizable=yes,scrollbars=yes'
    );
    if (_helpPopup) {
        try { _helpPopup.focus(); } catch (_) { }
    }

    // Tell the server what context we're in
    if (context) {
        notifyHelpAgent(context, userId);
    }
}

/**
 * Close the help popup, if this tab is the one that opened it.
 *
 * Called when the session ends. The help agent is a signed-in surface, and
 * leaving it open beside a locked Console would be confusing.
 *
 * This is the one window we can reliably close, because we kept the reference
 * that `window.open` returned. Designer, Explorer and Channels are opened
 * without keeping theirs, and the reference would not survive a Console reload
 * anyway, so those tabs lock instead of closing.
 *
 * A popup the user already closed, or one from a previous Console load, simply
 * is not here. That is not an error.
 */
export function closeHelpAgent() {
    if (!_helpPopup) return;
    try {
        if (!_helpPopup.closed) _helpPopup.close();
    } catch (_) {
        // Cross-origin rules can refuse this. Nothing more we can do, and it
        // must never stop a sign-out.
    }
    _helpPopup = null;
}

/**
 * Notify the server of the user's current app context.
 * Called on tab focus by each app (Console/Designer/Explorer).
 * The popup polls the server to read this.
 */
export function notifyHelpAgent(context, userId) {
    const dbamUrl = getDbamUrl();
    fetch(`${dbamUrl}/dbam/v1/help/context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId: userId || 'default',
            context: context,
        }),
    }).catch(() => {
        // Silent — context tracking is best-effort
    });
}
